import React, { useState, useEffect } from 'react';
import { Users, Search, Lock, Unlock, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import { toast } from 'react-toastify';
import AccountEditModal from './AccountEditModal';

const AdminAccountList = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingAccountId, setEditingAccountId] = useState(null);

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10; 
    const [jumpPage, setJumpPage] = useState('');

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const res = await adminStaffService.getAllAccounts();
            setAccounts(Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
        } catch (error) {
            console.error("Lỗi lấy danh sách tài khoản:", error);
            toast.error("Không thể tải danh sách tài khoản");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    // Tự động quay về trang 1 khi tìm kiếm
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handleToggleLock = async (id, isHidden) => {
        const action = isHidden ? "MỞ KHÓA" : "KHÓA";
        if (window.confirm(`Bạn có chắc chắn muốn ${action} tài khoản này?`)) {
            try {
                await adminStaffService.toggleLockAccount(id);
                fetchAccounts();
                toast.success(`${action} tài khoản thành công!`);
            } catch (error) {
                toast.error(`Lỗi khi ${action} tài khoản!`);
            }
        }
    };

    const handleChangeRole = async (id, currentRole, newRole) => {
        if (currentRole === newRole) return;
        if (window.confirm(`Chuyển quyền tài khoản thành ${newRole === 'ROLE_STAFF' ? 'Nhân viên' : 'Khách hàng'}?`)) {
            try {
                await adminStaffService.changeAccountRole(id, newRole);
                toast.success("Cập nhật phân quyền thành công!");
                fetchAccounts();
            } catch (error) {
                toast.error("Lỗi khi thay đổi phân quyền!");
                fetchAccounts();
            }
        }
    };

    // --- Filter & Pagination Logic ---
    const filteredAccounts = accounts.filter(acc => 
        (acc.username && acc.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (acc.accountName && acc.accountName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (acc.email && acc.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (acc.phone && acc.phone.includes(searchTerm))
    );

    const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage) || 1;
    const validCurrentPage = Math.min(currentPage, totalPages);
    const paginatedAccounts = filteredAccounts.slice((validCurrentPage - 1) * itemsPerPage, validCurrentPage * itemsPerPage);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleJumpPage = () => {
        const page = parseInt(jumpPage, 10);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setJumpPage('');
        } else {
            setJumpPage('');
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Users size={26} className="text-tnc-orange"/> Quản lý Tài khoản
                </h2>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                    <div className="relative w-full max-w-md">
                        <input 
                            type="text" 
                            placeholder="Tìm theo username, tên, email, sđt..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange text-sm"
                        />
                        <Search size={18} className="absolute left-3 top-3 text-gray-400" />
                    </div>
                    <div className="text-sm text-gray-500 font-medium">Tổng: <span className="text-tnc-orange font-bold">{filteredAccounts.length}</span> user</div>
                </div>

                <div className="overflow-x-auto min-h-[400px]">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                        <thead>
                            <tr className="bg-[#002855] text-white text-sm">
                                <th className="px-4 py-3 font-medium">Thông tin User</th>
                                <th className="px-4 py-3 font-medium">Liên hệ</th>
                                <th className="px-4 py-3 font-medium">Phân quyền</th>
                                <th className="px-4 py-3 font-medium text-center">Trạng thái</th>
                                <th className="px-4 py-3 font-medium text-right">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {loading ? <tr><td colSpan="5" className="text-center py-10">Đang tải dữ liệu...</td></tr> : 
                            filteredAccounts.length === 0 ? <tr><td colSpan="5" className="text-center py-10 text-gray-500">Không tìm thấy tài khoản!</td></tr> :
                            paginatedAccounts.map((acc) => (
                                <tr key={acc.accountId} className={`border-b transition-colors hover:bg-orange-50/30 ${acc.isHidden ? 'bg-red-50/30' : ''}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-bold text-gray-800">{acc.accountName || acc.fullName || 'Thành viên'}</div>
                                        <div className="text-xs text-blue-600 font-mono mt-0.5">@{acc.username}</div>
                                    </td>
                                    
                                    <td className="px-4 py-3">
                                        <div className="text-gray-800">{acc.phone || 'Chưa cập nhật'}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">{acc.email || 'Chưa cập nhật'}</div>
                                    </td>

                                    <td className="px-4 py-3">
                                        {acc.role === 'ROLE_ADMIN' ? (
                                            <span className="inline-block text-xs font-bold px-2.5 py-1.5 rounded bg-purple-100 text-purple-700 border border-purple-200">
                                                Quản trị viên
                                            </span>
                                        ) : (
                                            <select 
                                                value={acc.role} 
                                                onChange={(e) => handleChangeRole(acc.accountId, acc.role, e.target.value)}
                                                className={`text-xs font-bold px-2 py-1.5 rounded outline-none cursor-pointer border ${
                                                    acc.role === 'ROLE_STAFF' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                                    'bg-gray-100 text-gray-700 border-gray-200'
                                                }`}
                                            >
                                                <option value="ROLE_CUSTOMER">Khách hàng</option>
                                                <option value="ROLE_STAFF">Nhân viên (Staff)</option>
                                            </select>
                                        )}
                                    </td>

                                    <td className="px-4 py-3 text-center">
                                        {acc.isHidden ? (
                                            <span className="bg-red-100 text-red-600 px-2.5 py-1 rounded-full text-xs font-bold">Bị Khóa</span>
                                        ) : (
                                            <span className="bg-green-100 text-green-600 px-2.5 py-1 rounded-full text-xs font-bold">Hoạt động</span>
                                        )}
                                    </td>

                                    <td className="px-4 py-3 flex justify-end">
                                        <button onClick={(e) => {
                                                e.stopPropagation(); 
                                                setEditingAccountId(acc.accountId);
                                            }}
                                            className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors text-xs font-bold mr-1"
                                        >
                                            Chi tiết
                                        </button>
                                        <button 
                                            onClick={() => handleToggleLock(acc.accountId, acc.isHidden)} 
                                            disabled={acc.role === 'ROLE_ADMIN'}
                                            className={`p-2 rounded-lg transition-colors flex items-center gap-1 text-xs font-bold ${
                                                acc.role === 'ROLE_ADMIN'
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50' 
                                                : acc.isHidden 
                                                ? 'bg-green-50 text-green-600 hover:bg-green-600 hover:text-white' 
                                                : 'bg-red-50 text-red-500 hover:bg-red-600 hover:text-white'
                                            }`}
                                        >
                                            {acc.isHidden ? <><Unlock size={16} /> Mở Khóa</> : <><Lock size={16} /> Khóa</>}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* --- Phân trang Tài Khoản --- */}
                {!loading && totalPages > 1 && (
                    <div className="flex flex-wrap justify-center items-center gap-4 p-4 border-t border-gray-200 bg-white">
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={() => handlePageChange(validCurrentPage - 1)} 
                                disabled={validCurrentPage === 1}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            <div className="flex items-center gap-1 overflow-x-auto max-w-[50vw] scrollbar-hide">
                                {[...Array(totalPages)].map((_, i) => {
                                    const page = i + 1;
                                    return (
                                        <button 
                                            key={page} 
                                            onClick={() => handlePageChange(page)}
                                            className={`w-8 h-8 flex-shrink-0 rounded-lg border font-bold transition-colors shadow-sm
                                                ${validCurrentPage === page 
                                                    ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-tnc-orange hover:border-tnc-orange'
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    )
                                })}
                            </div>

                            <button 
                                onClick={() => handlePageChange(validCurrentPage + 1)} 
                                disabled={validCurrentPage === totalPages}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>

                        <div className="flex items-center gap-2 border-l border-gray-300 pl-4">
                            <span className="text-sm text-gray-600 font-medium">Đến trang:</span>
                            <input
                                type="number"
                                min="1"
                                max={totalPages}
                                value={jumpPage}
                                onChange={(e) => setJumpPage(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleJumpPage()}
                                placeholder={validCurrentPage.toString()}
                                className="w-14 h-8 px-2 text-center border border-gray-200 rounded-lg outline-none focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange font-bold text-gray-700 shadow-sm"
                            />
                            <button
                                onClick={handleJumpPage}
                                className="px-3 h-8 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors shadow-sm"
                            >
                                Đi
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {editingAccountId && (
                <AccountEditModal 
                    accountId={editingAccountId} 
                    onClose={() => setEditingAccountId(null)} 
                    onSuccess={() => {
                        setEditingAccountId(null);
                        fetchAccounts(); 
                    }}
                />
            )}
        </div>
    );
};

export default AdminAccountList;